/**
 * Skeleton — shimmer placeholder shown while content loads.
 * Replaces plain "Loading..." text across the app.
 */

interface SkeletonProps {
  /** Width CSS value. Defaults to "100%" */
  width?: string;
  /** Height CSS value. Defaults to "1rem" */
  height?: string;
  /** Border radius. Defaults to "var(--radius-sm)" */
  radius?: string;
  /** Repeat the skeleton N times vertically with gap */
  lines?: number;
  className?: string;
}

export function Skeleton({
  width = "100%",
  height = "1rem",
  radius = "var(--radius-sm)",
  lines = 1,
  className = "",
}: SkeletonProps) {
  if (lines > 1) {
    return (
      <div
        className={`skeleton-group ${className}`}
        role="status"
        aria-label="Loading"
      >
        {Array.from({ length: lines }, (_, i) => (
          <div
            key={i}
            className="skeleton"
            style={{
              width: i === lines - 1 ? "60%" : width,
              height,
              borderRadius: radius,
            }}
          />
        ))}
        <span className="sr-only">Loading…</span>
      </div>
    );
  }

  return (
    <div
      className={`skeleton ${className}`}
      role="status"
      aria-label="Loading"
      style={{ width, height, borderRadius: radius }}
    >
      <span className="sr-only">Loading…</span>
    </div>
  );
}

/** Page-level skeleton for full-page loading states */
export function PageSkeleton() {
  return (
    <div className="page-skeleton" role="status" aria-label="Loading page">
      <Skeleton width="40%" height="2rem" />
      <Skeleton width="60%" height="1rem" />
      <div className="skeleton-group" style={{ marginTop: "1.5rem" }}>
        <Skeleton height="6rem" radius="var(--radius-md)" />
        <Skeleton height="6rem" radius="var(--radius-md)" />
      </div>
      <Skeleton lines={4} />
      <span className="sr-only">Loading…</span>
    </div>
  );
}

/** Inline skeleton for cases where loading sits inside existing content */
export function InlineSkeleton({ width = "8rem" }: { width?: string }) {
  return (
    <span
      className="skeleton skeleton--inline"
      role="status"
      aria-label="Loading"
      style={{ width, height: "1em", borderRadius: "var(--radius-sm)" }}
    >
      <span className="sr-only">Loading…</span>
    </span>
  );
}
